import React, { useState, useEffect, useRef, useMemo } from "react";
import styles from "./CommentSection.module.css";
import { post, upload } from "../../utils/request";
import { useToast } from "../Toast/Toast";
import {
  getFilePreviewUrl,
  getFilePreviewUrlList,
} from "../../utils/attachmentPreview";
import { t, noAutoTranslateUserTextProps, mergeReaderNameClass } from "../../utils/i18n";
import {
  createPollAndGetVoteId,
  getPollErrorMessage,
  validatePollDraft,
} from "../../utils/poll";
import VoteSection from "../VoteSection/VoteSection";
import ModeratorVerifiedBadge from "../ModeratorVerifiedBadge/ModeratorVerifiedBadge";
import { createTopicModeratorChecker } from "../../utils/topicModerators";
import UserProfilePanel from "../UserProfilePanel/UserProfilePanel";

const COMMENT_UPLOAD_DIRECTORY =
  process.env.REACT_APP_UPLOAD_DIRECTORY || "";
const COMMENT_UPLOAD_STORAGE_CODE =
  process.env.REACT_APP_UPLOAD_STORAGE_CODE || "";
const COMMENT_MAX_LENGTH = 1000;
const resolveVoteId = item => item?.voteId ?? item?.voteID ?? item?.vetoId;

const EMPTY_EDITOR_HTML = /^(?:\s|&nbsp;|<br\s*\/?>)*$/i;

const escapeHtml = text =>
  String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/** 提交前：将 contenteditable 的 div/p 转为 br，便于后端存储换行 */
const normalizeCommentContentForSubmit = html => {
  let result = String(html ?? "").trim();
  if (!result || EMPTY_EDITOR_HTML.test(result)) return "";

  result = result
    .replace(/<\/div>\s*<div[^>]*>/gi, "<br>")
    .replace(/<\/p>\s*<p[^>]*>/gi, "<br>")
    .replace(/^<div[^>]*>/i, "")
    .replace(/<\/div>$/i, "")
    .replace(/^<p[^>]*>/i, "")
    .replace(/<\/p>$/i, "")
    .replace(/<div[^>]*>/gi, "")
    .replace(/<\/div>/gi, "<br>")
    .replace(/<p[^>]*>/gi, "")
    .replace(/<\/p>/gi, "<br>")
    .replace(/(<br\s*\/?>\s*){2,}/gi, "<br>")
    .replace(/^(?:<br\s*\/?>\s*)+|(?:\s*<br\s*\/?>)+$/gi, "");

  return result.trim();
};

const getCommentPlainTextFromHtml = html =>
  String(html ?? "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/\u00a0/g, " ");

const getCommentPlainTextLength = html =>
  getCommentPlainTextFromHtml(html).length;

/** 超出字数上限时截断编辑器纯文本 */
const truncateCommentEditor = el => {
  if (!el) return;

  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
  const textNodes = [];
  let total = 0;
  let node;

  while ((node = walker.nextNode())) {
    textNodes.push(node);
    total += node.textContent.length;
  }

  if (total <= COMMENT_MAX_LENGTH) return;

  let remaining = COMMENT_MAX_LENGTH;
  textNodes.forEach(textNode => {
    const len = textNode.textContent.length;
    if (remaining <= 0) {
      textNode.textContent = "";
    } else if (len > remaining) {
      textNode.textContent = textNode.textContent.slice(0, remaining);
      remaining = 0;
    } else {
      remaining -= len;
    }
  });
};

/** 展示前：纯文本换行转 br；已有 HTML 则原样展示 */
const formatCommentContentForDisplay = content => {
  const raw = String(content ?? "");
  if (!raw.trim()) return "";

  if (/<[a-z][\s\S]*>/i.test(raw)) {
    return raw;
  }

  return escapeHtml(raw.replace(/\r\n/g, "\n")).replace(/\n/g, "<br>");
};

const UserDisplayNameWithModerator = ({
  displayName,
  userId,
  username,
  item,
  suppressTranslate,
  nameClass,
  topicModChecker,
}) => (
  <span className={styles.userNameWithMod}>
    <span
      className={mergeReaderNameClass(suppressTranslate, nameClass)}
      {...noAutoTranslateUserTextProps}
    >
      {displayName}
    </span>
    {topicModChecker(userId, username, item) ? (
      <ModeratorVerifiedBadge />
    ) : null}
  </span>
);

// 默认头像在客户端简单缓存一份，避免多次计算路径
let cachedDefaultAvatarSrc = null;
const getDefaultAvatarSrc = () => {
  if (cachedDefaultAvatarSrc) return cachedDefaultAvatarSrc;
  cachedDefaultAvatarSrc = `${process.env.PUBLIC_URL || ""}/defaultAvatar.png`;
  return cachedDefaultAvatarSrc;
};

/**
 * 评论区共用组件
 *
 * @param {string}   category       - 评论分类: GAME / COLLECTION / ARTICLE / VOTE 等
 * @param {number|string} objectId  - 评论目标对象 ID
 * @param {boolean}  isLoggedIn     - 当前用户是否已登录
 * @param {function} onRequireLogin - 未登录时的回调（用于弹出登录框）
 * @param {number}   totalFromDetail - 外部传入的总评论数（如详情接口的 commentCnt）
 * @param {Array<{id?: string, username?: string}>|null} [topicModerators] - 话题版主列表（用于 POST 等场景下在昵称后显示认证标）
 */
const CommentSection = ({
  category,
  objectId,
  isLoggedIn,
  onRequireLogin,
  hideTabs = false,
  onRatingUpdated,
  totalFromDetail,
  onCommentTotalChange,
  topicModerators = null,
}) => {
  const toast = useToast();
  const topicModChecker = useMemo(
    () => createTopicModeratorChecker(topicModerators),
    [topicModerators],
  );
  const suppressReaderNameAutoTranslate =
    category === "GAME" ||
    category === "COLLECTION" ||
    category === "POST";
  // 评论列表相关
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [commentTotal, setCommentTotal] = useState(0);
  const [commentPage, setCommentPage] = useState(1);
  const [hasMoreComments, setHasMoreComments] = useState(true);
  const [reviewTab, setReviewTab] = useState("hot");
  const [activeReviewId, setActiveReviewId] = useState(null);

  // 发表评论弹窗
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentContent, setCommentContent] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewImageIndex, setPreviewImageIndex] = useState(-1);
  const [commentAttachmentUUID, setCommentAttachmentUUID] = useState("");
  const [commentAddVote, setCommentAddVote] = useState(false);
  const [commentVoteTitle, setCommentVoteTitle] = useState("");
  const [commentVoteChoiceType, setCommentVoteChoiceType] = useState("SINGLE");
  const [commentVoteMaxSelected, setCommentVoteMaxSelected] = useState(2);
  const [commentVoteOptions, setCommentVoteOptions] = useState(["", ""]);
  /** 列表/详情中评论附件大图预览 { urls, index } */
  const [attachmentGalleryPreview, setAttachmentGalleryPreview] =
    useState(null);

  const resetCommentVoteFields = () => {
    setCommentAddVote(false);
    setCommentVoteTitle("");
    setCommentVoteChoiceType("SINGLE");
    setCommentVoteMaxSelected(2);
    setCommentVoteOptions(["", ""]);
  };

  const handleCommentVoteOptionChange = (index, value) => {
    setCommentVoteOptions(prev =>
      prev.map((item, i) => (i === index ? value : item)),
    );
  };

  const handleAddCommentVoteOption = () => {
    setCommentVoteOptions(prev => (prev.length >= 8 ? prev : [...prev, ""]));
  };

  const handleRemoveCommentVoteOption = index => {
    setCommentVoteOptions(prev =>
      prev.length <= 2 ? prev : prev.filter((_, i) => i !== index),
    );
  };

  // 评分
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  // 点赞
  const [thumbLoading, setThumbLoading] = useState(false);

  // 富文本编辑器（contenteditable）
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);

  const moveCaretToEnd = () => {
    const el = editorRef.current;
    if (!el) return;

    el.focus();
    const selection = window.getSelection?.();
    if (!selection) return;

    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);

    selection.removeAllRanges();
    selection.addRange(range);
  };

  // 仅在“焦点进来但用户没有指定光标位置”时，把光标挪到末尾。
  // 避免用户点击/选中中间内容时光标被我们再次强制跳到最后。
  const handleEditorFocus = () => {
    requestAnimationFrame(() => {
      const el = editorRef.current;
      const selection = window.getSelection?.();
      if (!el || !selection || selection.rangeCount === 0) {
        moveCaretToEnd();
        return;
      }

      const range = selection.getRangeAt(0);
      // 用户通常会在编辑器内部点击到某个位置，此时不应强制移动。
      if (el.contains(range.startContainer)) return;

      moveCaretToEnd();
    });
  };

  const handleEditorInput = () => {
    if (!editorRef.current) return;
    truncateCommentEditor(editorRef.current);
    setCommentContent(editorRef.current.innerHTML);
  };

  const handleEditorBeforeInput = e => {
    const el = editorRef.current;
    if (!el) return;

    const currentLen = (el.innerText || el.textContent || "").length;
    if (currentLen >= COMMENT_MAX_LENGTH) {
      if (
        e.inputType === "insertText" ||
        e.inputType === "insertFromPaste" ||
        e.inputType === "insertCompositionText"
      ) {
        e.preventDefault();
      }
      return;
    }

    if (e.inputType !== "insertFromPaste" && e.inputType !== "insertText") {
      return;
    }

    const insertText =
      e.inputType === "insertFromPaste"
        ? e.dataTransfer?.getData("text/plain") || ""
        : e.data || "";

    if (!insertText) return;

    const allowed = COMMENT_MAX_LENGTH - currentLen;
    if (insertText.length > allowed) {
      e.preventDefault();
      if (allowed > 0) {
        document.execCommand("insertText", false, insertText.slice(0, allowed));
        setCommentContent(el.innerHTML);
      }
    }
  };

  const generateCommentAttachmentUUID = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  };

  const handlePickImageClick = () => {
    imageInputRef.current?.click();
  };

  const handleImageSelected = e => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const remain = Math.max(0, 9 - selectedImages.length);
    if (remain === 0) {
      toast.warning(t("comment.error.maxImages", "You can only upload up to 9 images"));
      e.target.value = "";
      return;
    }
    const picked = files.slice(0, remain);
    if (files.length > remain) {
      toast.warning(t("comment.error.maxImages", "You can only upload up to 9 images"));
    }

    const maxSize = 2 * 1024 * 1024;
    const validFiles = picked.filter(file => {
      if (!file.type.startsWith("image/")) return false;
      if (file.size > maxSize) {
        toast.warning(`${file.name} is too large, it has been skipped`);
        return false;
      }
      return true;
    });

    const readAsDataUrl = file =>
      new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = () =>
          resolve(typeof reader.result === "string" ? reader.result : "");
        reader.onerror = () => resolve("");
        reader.readAsDataURL(file);
      });

    const attachmentUUID =
      commentAttachmentUUID || generateCommentAttachmentUUID();
    if (!commentAttachmentUUID) {
      setCommentAttachmentUUID(attachmentUUID);
    }

    const uploadOne = async file => {
      const formData = new FormData();
      if (COMMENT_UPLOAD_DIRECTORY) {
        formData.append("directory", COMMENT_UPLOAD_DIRECTORY);
      }
      formData.append("file", file);
      formData.append("attachmentUUID", attachmentUUID);
      formData.append("fileName", file.name || "comment-image");
      if (COMMENT_UPLOAD_STORAGE_CODE) {
        formData.append("storageCode", COMMENT_UPLOAD_STORAGE_CODE);
      }
      await upload("/file/multipart", formData);
      const localPreview = await readAsDataUrl(file);
      return localPreview;
    };

    Promise.all(validFiles.map(file => uploadOne(file)))
      .then(urls => {
        const nextImages = urls
          .filter(Boolean)
          .map((src, idx) => ({
            id: `${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 8)}`,
            src,
          }));
        if (nextImages.length > 0) {
          setSelectedImages(prev => [...prev, ...nextImages].slice(0, 9));
        }
      })
      .catch(error => {
        console.error("Failed to upload comment images:", error);
        toast.error(error?.message || t("comment.error.uploadImages", "Failed to upload images"));
      });

    e.target.value = "";
  };

  const handleRemoveSelectedImage = imageId => {
    setSelectedImages(prev => prev.filter(item => item.id !== imageId));
  };

  const handleOpenImagePreview = index => {
    if (!Array.isArray(selectedImages) || selectedImages.length === 0) return;
    if (index < 0 || index >= selectedImages.length) return;
    setPreviewImageIndex(index);
  };

  const handleCloseImagePreview = () => {
    setPreviewImageIndex(-1);
  };

  const handlePreviewPrev = () => {
    if (!selectedImages.length) return;
    setPreviewImageIndex(prev =>
      prev <= 0 ? selectedImages.length - 1 : prev - 1,
    );
  };

  const handlePreviewNext = () => {
    if (!selectedImages.length) return;
    setPreviewImageIndex(prev =>
      prev >= selectedImages.length - 1 ? 0 : prev + 1,
    );
  };

  useEffect(() => {
    if (previewImageIndex < 0) return;
    const onKeyDown = e => {
      if (e.key === "Escape") {
        handleCloseImagePreview();
      } else if (e.key === "ArrowLeft") {
        handlePreviewPrev();
      } else if (e.key === "ArrowRight") {
        handlePreviewNext();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [previewImageIndex, selectedImages.length]);

  const closeAttachmentGallery = () => setAttachmentGalleryPreview(null);

  const openAttachmentGallery = (urls, index = 0) => {
    if (!urls || urls.length === 0) return;
    const i = Math.max(0, Math.min(index, urls.length - 1));
    setAttachmentGalleryPreview({ urls, index: i });
  };

  const galleryPrev = () => {
    setAttachmentGalleryPreview(prev => {
      if (!prev?.urls?.length) return null;
      const next =
        prev.index <= 0 ? prev.urls.length - 1 : prev.index - 1;
      return { ...prev, index: next };
    });
  };

  const galleryNext = () => {
    setAttachmentGalleryPreview(prev => {
      if (!prev?.urls?.length) return null;
      const next =
        prev.index >= prev.urls.length - 1 ? 0 : prev.index + 1;
      return { ...prev, index: next };
    });
  };

  useEffect(() => {
    if (!attachmentGalleryPreview) return;
    const onKeyDown = e => {
      if (e.key === "Escape") {
        setAttachmentGalleryPreview(null);
      } else if (e.key === "ArrowLeft") {
        setAttachmentGalleryPreview(prev => {
          if (!prev?.urls?.length) return null;
          const next =
            prev.index <= 0 ? prev.urls.length - 1 : prev.index - 1;
          return { ...prev, index: next };
        });
      } else if (e.key === "ArrowRight") {
        setAttachmentGalleryPreview(prev => {
          if (!prev?.urls?.length) return null;
          const next =
            prev.index >= prev.urls.length - 1 ? 0 : prev.index + 1;
          return { ...prev, index: next };
        });
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [attachmentGalleryPreview]);

  // 回复目标：{ rootId, pid, username }，null 表示发表一级评论
  const [replyTo, setReplyTo] = useState(null);

  /** 评论区点头像打开的用户主页 */
  const [profileTarget, setProfileTarget] = useState(null);

  // 二级评论的分页记录：{ rootId: number }
  const [replyPages, setReplyPages] = useState({});
  // 二级评论的加载状态：{ rootId: boolean }
  const [replyLoading, setReplyLoading] = useState({});
  // 二级评论是否还有更多：{ rootId: boolean }
  const [replyHasMore, setReplyHasMore] = useState({});

  // 显示用的评论总数：默认使用详情接口传入的 commentCnt，没有则回退到评论接口返回的 total
  const [displayCommentTotal, setDisplayCommentTotal] = useState(
    typeof totalFromDetail === "number" ? totalFromDetail : 0,
  );

  // 当外部详情的 commentCnt 或内部 commentTotal 变化时，同步刷新展示用的总数
  useEffect(() => {
    if (typeof totalFromDetail === "number") {
      setDisplayCommentTotal(totalFromDetail);
    } else {
      setDisplayCommentTotal(commentTotal);
    }
  }, [totalFromDetail, commentTotal]);

  // 无限下滑触发器
  const loadMoreRef = useRef(null);
  const loadMoreRepliesRef = useRef(null);

  // 监听滚动到列表底部，触发下一页加载（无限下滑）
  useEffect(() => {
    if (activeReviewId) return;
    if (!loadMoreRef.current) return;
    if (!hasMoreComments) return;

    const target = loadMoreRef.current;
    const observer = new IntersectionObserver(
      entries => {
        const first = entries[0];
        if (
          first.isIntersecting &&
          !loadingMore &&
          !loadingComments &&
          hasMoreComments
        ) {
          const nextPage = commentPage + 1;
          setCommentPage(nextPage);
          fetchComments(nextPage, true);
        }
      },
      {
        root: null,
        // 提前 200px 预加载，避免到底部才触发造成空白感
        rootMargin: "200px 0px 0px 0px",
        threshold: 0,
      },
    );

    observer.observe(target);
    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReviewId, commentPage, hasMoreComments, loadingMore, loadingComments]);

  // 每页条数
  const PAGE_SIZE = 10;

  const getDisplayName = item => item.username || `User ${item.userId}`;

  const getReplyCount = review => {
    const loadedCount = Array.isArray(review?.replyList)
      ? review.replyList.length
      : 0;
    const parsedReplyTotal = Number(
      review?.replyTotal ?? review?.replyCnt,
    );
    if (Number.isFinite(parsedReplyTotal) && parsedReplyTotal > 0) {
      return Math.max(parsedReplyTotal, loadedCount);
    }
    return loadedCount;
  };

  const getRootReplies = review => {
    if (!Array.isArray(review?.replyList) || review.replyList.length === 0) {
      return [];
    }
    const directReplies = review.replyList.filter(reply => reply.pid === review.id);
    return directReplies.length > 0 ? directReplies : review.replyList;
  };

  const normalizeUserRating = val => {
    const num = Number(val);
    if (!Number.isFinite(num)) return 0;
    return Math.max(0, Math.min(5, Math.round(num)));
  };

  /** 列表星条：与 /social/comment/view 返回的每条 comment.rating 对齐 */
  const shouldRenderRatingStars = ["GAME", "COLLECTION", "ARTICLE"].includes(
    category,
  );

  const getReplyTargetName = (review, reply) => {
    if (!reply) return "";
    if (reply.replyUsername) return reply.replyUsername;
    if (reply.pid === review?.id) return getDisplayName(review);

    const parentReply = Array.isArray(review?.replyList)
      ? review.replyList.find(item => item.id === reply.pid)
      : null;

    return parentReply ? getDisplayName(parentReply) : "";
  };

  const resolveCommentAvatar = comment => {
    if (!comment?.avatarId) return;
    getFilePreviewUrl(comment.avatarId)
      .then(url => {
        if (!url) return;
        setComments(prev =>
          prev.map(item =>
            item.id === comment.id ? { ...item, avatarUrl: url } : item,
          ),
        );
      })
      .catch(() => {});
  };

  const resolveReplyAvatar = (rootId, reply) => {
    if (!reply?.avatarId) return;
    getFilePreviewUrl(reply.avatarId)
      .then(url => {
        if (!url) return;
        setComments(prev =>
          prev.map(item =>
            item.id !== rootId || !Array.isArray(item.replyList)
              ? item
              : {
                  ...item,
                  replyList: item.replyList.map(currentReply =>
                    currentReply.id === reply.id
                      ? { ...currentReply, avatarUrl: url }
                      : currentReply,
                  ),
                },
          ),
        );
      })
      .catch(() => {});
  };

  const resolveCommentAttachmentImages = comment => {
    const uuid =
      comment.attachmentUUID ||
      comment.attachmentUuid ||
      "";
    if (!uuid) return;
    getFilePreviewUrlList(uuid)
      .then(urls => {
        if (!urls || urls.length === 0) return;
        setComments(prev =>
          prev.map(item =>
            item.id === comment.id
              ? { ...item, attachmentImageUrls: urls }
              : item,
          ),
        );
      })
      .catch(() => {});
  };

  const hydrateAvatarUrls = commentList => {
    commentList.forEach(comment => {
      resolveCommentAvatar(comment);
      if (!Array.isArray(comment.replyList)) return;
      comment.replyList.forEach(reply => {
        resolveReplyAvatar(comment.id, reply);
      });
    });
  };

  const hydrateAttachmentImages = commentList => {
    commentList.forEach(comment => {
      resolveCommentAttachmentImages(comment);
    });
  };

  // ========== 获取评论列表（支持分页 / 追加） ==========
  const fetchComments = async (pageNo = 1, append = false) => {
    if (!objectId) return;
    if (append) {
      if (loadingMore || !hasMoreComments) return;
      setLoadingMore(true);
    } else {
      setLoadingComments(true);
    }

    try {
      const res = await post("/social/comment/view", {
        data: {
          category,
          objectId,
          // 根据当前 Tab 传入排序方式：Latest -> TIME，Hot -> LIKED
          sort: reviewTab === "latest" ? "TIME" : "LIKED",
        },
        pageNo,
        pageSize: PAGE_SIZE,
        sortingFieldList: [],
      });

      console.log("评论列表=>", res);

      if (res && res.data && Array.isArray(res.data.data)) {
        // 按接口返回顺序展示（由后端根据 sort 排序）
        const rawList = res.data.data;

        // 规范化字段：
        // - avatarId / avatarUrl：用于头像懒加载
        // - isThumbed：直接使用接口返回的 liked 字段
        const normalized = rawList.map(item => {
          const previewReplies = Array.isArray(item.replyList)
            ? item.replyList.map(r => ({
                ...r,
                rating: normalizeUserRating(r.rating),
                avatarUrl: "",
                avatarId: r.avatar || "",
                voteId: resolveVoteId(r),
                isThumbed: !!r.liked,
              }))
            : item.replyList;
          const replyTotalFromApi = Number(item.replyTotal ?? item.replyCnt);
          const replyTotal =
            Number.isFinite(replyTotalFromApi) && replyTotalFromApi > 0
              ? replyTotalFromApi
              : Array.isArray(previewReplies)
                ? previewReplies.length
                : 0;

          return {
            ...item,
            rating: normalizeUserRating(item.rating),
            avatarUrl: "",
            avatarId: item.avatar || "",
            voteId: resolveVoteId(item),
            attachmentUUID: item.attachmentUUID ?? item.attachmentUuid ?? "",
            attachmentImageUrls: [],
            isThumbed: !!item.liked,
            replyList: previewReplies,
            replyTotal,
          };
        });

        const total = res.data.total || 0;
        setCommentTotal(total);

        if (append) {
          setComments(prev => {
            const currentIds = new Set(prev.map(c => c.id));
            const uniqueNew = normalized.filter(c => !currentIds.has(c.id));
            return [...prev, ...uniqueNew];
          });
        } else {
          setComments(normalized);
        }

        // 根据总数和当前页判断是否还有更多数据
        const totalPages = Math.ceil(total / PAGE_SIZE);
        setHasMoreComments(pageNo < totalPages);

        hydrateAvatarUrls(normalized);
        hydrateAttachmentImages(normalized);
      } else {
        if (!append) {
          setComments([]);
          setCommentTotal(0);
          setHasMoreComments(false);
        }
      }
    } catch (error) {
      console.error("Failed to fetch comments:", error);
      if (!append) {
        setComments([]);
        setCommentTotal(0);
        setHasMoreComments(false);
      }
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoadingComments(false);
      }
    }
  };

  useEffect(() => {
    // 切换对象或 Tab 时重置分页，从第一页重新拉取
    setCommentPage(1);
    setHasMoreComments(true);
    setReplyPages({});
    setActiveReviewId(null);
    fetchComments(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectId, reviewTab]);

  // ========== 发表评论 ==========
  const handleWriteReviewClick = () => {
    if (isLoggedIn) {
      setReplyTo(null);
      setCommentContent("");
      setSelectedImages([]);
      setPreviewImageIndex(-1);
      setCommentAttachmentUUID(generateCommentAttachmentUUID());
      setRating(0);
      setShowCommentModal(true);
      // 立即清空编辑器内容（避免上一次内容残留）
      if (editorRef.current) editorRef.current.innerHTML = "";
    } else {
      onRequireLogin && onRequireLogin();
    }
  };

  // ========== 回复某条评论 ==========
  const handleReplyClick = (review, parentRootId = null) => {
    if (!isLoggedIn) {
      onRequireLogin && onRequireLogin();
      return;
    }

    // 如果没有 parentRootId，说明正在回复一级评论 (一级评论的 rootId 应该是其自己的 id)
    const rootId = parentRootId ? parentRootId : review.id;

    setReplyTo({
      rootId: rootId,
      pid: review.id,
      username: getDisplayName(review),
      userId: review.userId ?? review.userID,
      loginUsername:
        review.username != null ? String(review.username).trim() : "",
    });
    setCommentContent("");
    setSelectedImages([]);
    setPreviewImageIndex(-1);
    setCommentAttachmentUUID(generateCommentAttachmentUUID());
    setShowCommentModal(true);
    // 立即清空编辑器内容（避免上一次内容残留）
    if (editorRef.current) editorRef.current.innerHTML = "";
  };

  // ========== 获取更多二级评论 / 评论详情页加载回复 ==========
  const fetchMoreReplies = async (rootId, options = false) => {
    const { forceRefresh = false } =
      typeof options === "boolean" ? { forceRefresh: options } : options;

    if (replyLoading[rootId]) return;

    setReplyLoading(prev => ({ ...prev, [rootId]: true }));
    try {
      const pageNo = forceRefresh ? 1 : (replyPages[rootId] || 0) + 1;

      const res = await post("/social/comment/expand", {
        data: {
          category,
          objectId: rootId,
          sort: reviewTab === "latest" ? "TIME" : "LIKED",
        },
        pageNo,
        pageSize: PAGE_SIZE,
        sortingFieldList: [],
      });

      if (!(res && res.data && Array.isArray(res.data.data))) {
        setReplyHasMore(prev => ({ ...prev, [rootId]: false }));
        return;
      }

      const newReplies = res.data.data.map(c => ({
        ...c,
        rating: normalizeUserRating(c.rating),
        avatarUrl: "",
        avatarId: c.avatar || "",
        voteId: resolveVoteId(c),
        isThumbed: !!c.liked,
      }));
      const apiTotal = Number(res.data.total) || 0;
      let mergedReplies = [];
      let uniqueAddedCount = 0;

      setComments(prevComments =>
        prevComments.map(c => {
          if (c.id !== rootId) return c;

          const existingReplies = forceRefresh ? [] : c.replyList ?? [];
          const currentIds = new Set(existingReplies.map(r => r.id));
          const uniqueNew = newReplies.filter(r => !currentIds.has(r.id));
          uniqueAddedCount = uniqueNew.length;
          mergedReplies = [...existingReplies, ...uniqueNew];
          const resolvedTotal =
            apiTotal > 0
              ? Math.max(apiTotal, mergedReplies.length)
              : mergedReplies.length;

          return {
            ...c,
            replyList: mergedReplies,
            replyTotal: resolvedTotal,
          };
        }),
      );

      setReplyPages(prev => ({ ...prev, [rootId]: pageNo }));
      newReplies.forEach(reply => {
        resolveReplyAvatar(rootId, reply);
      });

      const hasMore =
        uniqueAddedCount > 0 &&
        (apiTotal > 0
          ? mergedReplies.length < apiTotal
          : newReplies.length >= PAGE_SIZE);
      setReplyHasMore(prev => ({ ...prev, [rootId]: hasMore }));
    } catch (error) {
      console.error("Failed to load more replies:", error);
      setReplyHasMore(prev => ({ ...prev, [rootId]: false }));
    } finally {
      setReplyLoading(prev => ({ ...prev, [rootId]: false }));
    }
  };

  const openCommentDetail = reviewId => {
    setActiveReviewId(reviewId);
  };

  // 进入评论详情页时，加载第一页回复（每页 10 条）
  useEffect(() => {
    if (!activeReviewId) return;
    setReplyHasMore(prev => ({ ...prev, [activeReviewId]: true }));
    fetchMoreReplies(activeReviewId, { forceRefresh: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReviewId]);

  // 评论详情：回复列表无限下滑加载
  useEffect(() => {
    if (!activeReviewId) return;
    if (!loadMoreRepliesRef.current) return;
    if (!replyHasMore[activeReviewId]) return;

    const target = loadMoreRepliesRef.current;
    const observer = new IntersectionObserver(
      entries => {
        const first = entries[0];
        if (
          first.isIntersecting &&
          !replyLoading[activeReviewId] &&
          replyHasMore[activeReviewId]
        ) {
          fetchMoreReplies(activeReviewId);
        }
      },
      {
        root: null,
        rootMargin: "200px 0px 0px 0px",
        threshold: 0,
      },
    );

    observer.observe(target);
    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReviewId, replyHasMore, replyLoading, replyPages]);

  const handleSubmitComment = async () => {
    const plain = getCommentPlainTextFromHtml(commentContent).trim();
    const plainLength = getCommentPlainTextLength(commentContent);
    const hasImage =
      /<img\b[^>]*>/i.test(String(commentContent || "")) ||
      selectedImages.length > 0;
    if (!plain && !hasImage) {
      toast.warning(t("comment.error.empty", "Please enter comment content"));
      return;
    }
    if (plainLength > COMMENT_MAX_LENGTH) {
      toast.warning(
        t(
          "comment.error.tooLong",
          "Comment cannot exceed {max} characters",
          { max: COMMENT_MAX_LENGTH },
        ),
      );
      return;
    }
    const voteValidation = validatePollDraft({
      title: commentVoteTitle,
      options: commentVoteOptions,
      choiceType: commentVoteChoiceType,
      maxSelected: commentVoteMaxSelected,
    });
    if (!replyTo && category === "COLLECTION" && commentAddVote) {
      if (!voteValidation.ok) {
        toast.warning(
          getPollErrorMessage(voteValidation, t, {
            questionRequired: "comment.poll.questionRequired",
            optionsRequired: "comment.poll.optionsRequired",
            maxSelectedInvalid: "comment.poll.maxSelectedRange",
          }),
        );
        return;
      }
    }
    setSubmittingComment(true);
    try {
      let voteIdForComment = null;
      if (!replyTo && category === "COLLECTION" && commentAddVote) {
        try {
          voteIdForComment = await createPollAndGetVoteId({
            title: commentVoteTitle,
            options: commentVoteOptions,
            choiceType: commentVoteChoiceType,
            maxSelected: commentVoteMaxSelected,
            parentId: Number(objectId) || 0,
            parentType: "COLLECTION",
          });
        } catch (pollError) {
          toast.error(getPollErrorMessage(pollError, t));
          return;
        }
      }

      // 构建并行请求列表：评论 + 评分（如有）
      const commentPayload = {
        category,
        content: normalizeCommentContentForSubmit(commentContent),
        objectId: Number(objectId),
        rootId: replyTo ? replyTo.rootId : 0,
        pid: replyTo ? replyTo.pid : undefined,
        attachmentUUID: commentAttachmentUUID || undefined,
      };
      if (voteIdForComment != null) {
        commentPayload.voteId = voteIdForComment;
      }
      // 与弹窗内 ratingSection 打星一致：1～5 星对应 1～5，未选星不传
      if (!replyTo && rating > 0) {
        commentPayload.rating = rating;
      }
      const commentPromise = post("/social/comment", commentPayload);

      const promises = [commentPromise];

      // 一级评论 + 分类为 GAME 或 COLLECTION + 选了评分，同时提交评分
      if (
        !replyTo &&
        (category === "GAME" || category === "COLLECTION") &&
        rating > 0
      ) {
        const ratingPromise = post("/game/rating/save", {
          gameId: Number(objectId),
          rating: String(rating),
        });
        promises.push(ratingPromise);
      }

      const [commentRes, ratingRes] = await Promise.all(promises);

      if (commentRes && commentRes.success) {
        if (ratingRes && !ratingRes.success) {
          console.warn("评分提交失败:", ratingRes.message);
        }
        setShowCommentModal(false);
        setCommentContent("");
        setSelectedImages([]);
        setPreviewImageIndex(-1);
        setCommentAttachmentUUID("");
        resetCommentVoteFields();
        setRating(0);
        setReplyTo(null);
        // 提交成功后清空编辑器
        if (editorRef.current) editorRef.current.innerHTML = "";
        if (imageInputRef.current) imageInputRef.current.value = "";
        // 一级评论成功后，展示用评论总数 +1
        if (!replyTo) {
          setDisplayCommentTotal(prev => {
            const next = typeof prev === "number" ? prev + 1 : 1;
            if (typeof onCommentTotalChange === "function") {
              onCommentTotalChange(next);
            }
            return next;
          });
        }
        if (replyTo) {
          setComments(prev =>
            prev.map(comment =>
              comment.id === replyTo.rootId
                ? {
                    ...comment,
                    replyTotal: getReplyCount(comment) + 1,
                  }
                : comment,
            ),
          );
          fetchMoreReplies(replyTo.rootId, { forceRefresh: true });
        } else {
          // 更新评论列表
          fetchComments();
        }
        // 如果是游戏详情页并且提交了评分，通知父组件刷新评分展示
        if (
          !replyTo &&
          category === "GAME" &&
          rating > 0 &&
          typeof onRatingUpdated === "function"
        ) {
          onRatingUpdated();
        }
      } else {
        toast.error(commentRes.message || t("comment.error.submit", "Failed to submit comment"));
      }
    } catch (error) {
      console.error("Failed to submit comment:", error);
      toast.error(error.message);
    } finally {
      setSubmittingComment(false);
    }
  };

  // ========== 评论点赞 / 取消点赞 ==========
  const handleToggleCommentThumb = async (
    commentId,
    isCurrentlyThumbed,
  ) => {
    if (!isLoggedIn) {
      onRequireLogin && onRequireLogin();
      return;
    }
    if (thumbLoading) return;
    setThumbLoading(true);

    try {
      if (isCurrentlyThumbed) {
        // 取消点赞
        const res = await post("/social/thumb/cancel", {
          category: "COMMENT",
          objectId: commentId,
        });
        if (res && res.data) {
          // 这里原本用的是 res.success，其实取消点赞如果成功也可以根据返回来判断
          setComments(prev =>
            prev.map(c => {
              if (c.id === commentId) {
                return {
                  ...c,
                  isThumbed: false,
                  likedCnt: Math.max(0, (c.likedCnt || 0) - 1),
                };
              }
              // 如果操作的是子评论
              if (c.replyList && c.replyList.length > 0) {
                const hasChild = c.replyList.find(r => r.id === commentId);
                if (hasChild) {
                  return {
                    ...c,
                    replyList: c.replyList.map(r =>
                      r.id === commentId
                        ? {
                            ...r,
                            isThumbed: false,
                            likedCnt: Math.max(0, (r.likedCnt || 0) - 1),
                          }
                        : r,
                    ),
                  };
                }
              }
              return c;
            }),
          );
        } else {
          toast.error(res?.message || t("comment.error.cancelLike", "Failed to cancel like"));
        }
      } else {
        // 点赞
        const res = await post("/social/thumb/do", {
          category: "COMMENT",
          objectId: commentId,
        });
        if (res && res.data) {
          setComments(prev =>
            prev.map(c => {
              if (c.id === commentId) {
                return {
                  ...c,
                  isThumbed: true,
                  likedCnt: (c.likedCnt || 0) + 1,
                };
              }
              if (c.replyList && c.replyList.length > 0) {
                const hasChild = c.replyList.find(r => r.id === commentId);
                if (hasChild) {
                  return {
                    ...c,
                    replyList: c.replyList.map(r =>
                      r.id === commentId
                        ? {
                            ...r,
                            isThumbed: true,
                            likedCnt: (r.likedCnt || 0) + 1,
                          }
                        : r,
                    ),
                  };
                }
              }
              return c;
            }),
          );
        } else {
          toast.error(res?.message || t("comment.error.like", "Failed to like"));
        }
      }
    } catch (e) {
      console.error("Toggle comment thumb failed", e);
      toast.error(
        t("comment.error.toggleLike", "Failed to toggle comment thumb, please try again"),
      );
    } finally {
      setThumbLoading(false);
    }
  };

  const openUserProfile = (item, event) => {
    event?.stopPropagation?.();
    const uid = item?.userId ?? item?.userID;
    if (uid == null || String(uid).trim() === "") return;
    setProfileTarget({
      userId: uid,
      username: getDisplayName(item),
      avatarUrl: item.avatarUrl || "",
    });
  };

  const renderAvatar = (item, className, size = 32) => {
    const uid = item?.userId ?? item?.userID;
    const canOpenProfile = uid != null && String(uid).trim() !== "";
    const avatarImg = (
      <img
        className={[className, "notranslate"].filter(Boolean).join(" ")}
        src={item.avatarUrl || getDefaultAvatarSrc()}
        alt=""
        width={size}
        height={size}
        {...noAutoTranslateUserTextProps}
      />
    );

    if (!canOpenProfile) return avatarImg;

    return (
      <button
        type="button"
        className={styles.avatarButton}
        onClick={event => openUserProfile(item, event)}
        aria-label={t("userProfile.open", "View user profile")}
      >
        {avatarImg}
      </button>
    );
  };

  const renderLikeAction = (item, options = {}) => {
    const { compact = false, onClick } = options;
    return (
      <div
        className={`${styles.likeInfo} ${compact ? styles.likeInfoCompact : ""}`}
        style={{ color: item.isThumbed ? "#ef4444" : undefined }}
        onClick={event => {
          event.stopPropagation();
          if (typeof onClick === "function") {
            onClick(event);
            return;
          }
          handleToggleCommentThumb(item.id, item.isThumbed);
        }}
      >
        <svg
          width={compact ? "14" : "18"}
          height={compact ? "14" : "18"}
          viewBox="0 0 24 24"
          fill={item.isThumbed ? "#ef4444" : "none"}
          stroke={item.isThumbed ? "#ef4444" : "currentColor"}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" />
          <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
        </svg>
        <span className={styles.likeCount}>{Number(item.likedCnt || 0)}</span>
      </div>
    );
  };

  const renderReplyButton = (item, rootId = null, compact = false) => (
    <button
      className={`${styles.menuTrigger} ${compact ? styles.replyTriggerCompact : ""}`}
      onClick={event => {
        event.stopPropagation();
        handleReplyClick(item, rootId);
      }}
      aria-label={t("aria.reply", "Reply")}
    >
      <svg
        width={compact ? "16" : "18"}
        height={compact ? "16" : "18"}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9 5l-7 7 7 7" />
        <path d="M4 12h11a5 5 0 0 1 0 10h-3" />
      </svg>
    </button>
  );

  const renderReplyTree = (review, parentId, depth = 0) => {
    if (!Array.isArray(review?.replyList) || review.replyList.length === 0) {
      return null;
    }

    const directChildren = review.replyList.filter(reply => reply.pid === parentId);
    const children =
      depth === 0 && directChildren.length === 0
        ? review.replyList
        : directChildren;
    if (children.length === 0) return null;

    return children.map(reply => {
      const nestedReplies = renderReplyTree(review, reply.id, depth + 1);
      const replyTargetName = getReplyTargetName(review, reply);
      return (
        <div
          key={`reply-${reply.id}`}
          className={`${styles.replyItem} ${depth > 0 ? styles.replyItemNested : ""}`}
        >
          <div className={styles.replyHeader}>
            {renderAvatar(reply, styles.replyAvatar, 24)}
            <UserDisplayNameWithModerator
              displayName={getDisplayName(reply)}
              userId={reply.userId ?? reply.userID}
              username={reply.username}
              item={reply}
              suppressTranslate={suppressReaderNameAutoTranslate}
              nameClass={styles.userName}
              topicModChecker={topicModChecker}
            />
            {replyTargetName ? (
              <span className={styles.replyToText}>
                <span className={styles.replyActionText}>
                  {t("comment.reply.action", "Reply")}
                </span>
                <span
                  className={mergeReaderNameClass(
                    suppressReaderNameAutoTranslate,
                    styles.replyAtText,
                  )}
                  {...noAutoTranslateUserTextProps}
                >
                  @{replyTargetName}
                </span>
                {topicModChecker(null, replyTargetName) ? (
                  <ModeratorVerifiedBadge />
                ) : null}
              </span>
            ) : null}
            <span className={styles.timeAgo}>• {reply.createTime || "Just now"}</span>
          </div>

          <div
            className={`${styles.replyTextContent} notranslate`}
            {...noAutoTranslateUserTextProps}
            dangerouslySetInnerHTML={{
              __html: formatCommentContentForDisplay(reply.content),
            }}
          />

          <div className={styles.replyFooter}>
            <div className={styles.actionGroup}>
              {renderLikeAction(reply, { compact: true })}
              <div className={styles.menuWrapper}>
                {renderReplyButton(reply, review.id, true)}
              </div>
            </div>
          </div>

          {nestedReplies ? (
            <div className={styles.replyListContainer}>{nestedReplies}</div>
          ) : null}
        </div>
      );
    });
  };

  const activeReview = activeReviewId
    ? comments.find(comment => comment.id === activeReviewId) || null
    : null;
  const isCollectionTopLevelComment = !replyTo && category === "COLLECTION";
  const draftPlainText = getCommentPlainTextFromHtml(commentContent).trim();
  const draftPlainTextLength = getCommentPlainTextLength(commentContent);
  const draftHasImage =
    /<img\b[^>]*>/i.test(String(commentContent || "")) ||
    selectedImages.length > 0;
  const isCommentTooLong = draftPlainTextLength > COMMENT_MAX_LENGTH;
  const canSubmitCommentDraft =
    (!!draftPlainText || draftHasImage) && !isCommentTooLong;
  const activeReplyCount = activeReview ? getReplyCount(activeReview) : 0;
  const rootReplies = activeReview ? getRootReplies(activeReview) : [];
  const hasMoreDetailReplies = !!activeReview && !!replyHasMore[activeReview.id];

  const renderReviewAttachmentGrid = (urls, { stopClick = false } = {}) => {
    if (!urls || urls.length === 0) return null;
    const isSingle = urls.length === 1;
    return (
      <div
        className={`${styles.reviewAttachmentGrid} ${
          isSingle ? styles.reviewAttachmentGridSingle : ""
        }`}
        onClick={stopClick ? e => e.stopPropagation() : undefined}
        role={stopClick ? "presentation" : undefined}
      >
        {urls.map((src, idx) => (
          <button
            key={`${src}-${idx}`}
            type="button"
            className={`${styles.reviewAttachmentThumb} ${
              isSingle ? styles.reviewAttachmentThumbSingle : ""
            }`}
            onClick={e => {
              if (stopClick) e.stopPropagation();
              openAttachmentGallery(urls, idx);
            }}
          >
            <img src={src} alt="" />
          </button>
        ))}
      </div>
    );
  };

  // ========== 渲染 ==========
  return (
    <div>
      {/* 评论区标题 + 发表按钮 */}
      <div className={styles.commentHeader}>
        {activeReview ? (
          <>
            <div className={styles.detailHeaderInfo}>
              <button
                className={styles.backButton}
                onClick={() => setActiveReviewId(null)}
                aria-label={t("comment.aria.backToComments", "Back to comments")}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <div>
                <h3>{t("comment.detail.title", "Comment Detail")}</h3>
                <p className={styles.detailHeaderMetaText}>
                  <UserDisplayNameWithModerator
                    displayName={getDisplayName(activeReview)}
                    userId={activeReview.userId ?? activeReview.userID}
                    username={activeReview.username}
                    item={activeReview}
                    suppressTranslate={suppressReaderNameAutoTranslate}
                    topicModChecker={topicModChecker}
                  />
                </p>
              </div>
            </div>
          </>
        ) : (
          <>
            <h3>
              {t("comment.list.title", "Comments ({count})", {
                count: displayCommentTotal,
              })}
            </h3>
            <button className={styles.textBtn} onClick={handleWriteReviewClick}>
              {t("comment.header.writeReview", "+ Write a review")}
            </button>
          </>
        )}
      </div>

      {/* Hot / Latest 切换 */}
      {!hideTabs && !activeReview && (
        <div className={styles.subTabs}>
          <div
            className={`${styles.tabItem} ${reviewTab === "hot" ? styles.activeTab : ""}`}
            onClick={() => setReviewTab("hot")}
          >
            Hot
          </div>
          <div
            className={`${styles.tabItem} ${reviewTab === "latest" ? styles.activeTab : ""}`}
            onClick={() => setReviewTab("latest")}
          >
            Latest
          </div>
        </div>
      )}

      {/* 评论列表 */}
      {loadingComments && comments.length === 0 ? (
        <div className={styles.loadingState}>{t("comment.loading", "Loading...")}</div>
      ) : comments.length > 0 ? (
        activeReview ? (
          <div className={styles.detailLayout}>
            <div className={styles.detailHeroCard}>
              <div className={styles.detailHeroHeader}>
                <div className={styles.userInfo}>
                  {renderAvatar(activeReview, styles.userAvatarSmall)}
                  <div className={styles.userMetaInline}>
                    <UserDisplayNameWithModerator
                      displayName={getDisplayName(activeReview)}
                      userId={activeReview.userId ?? activeReview.userID}
                      username={activeReview.username}
                      item={activeReview}
                      suppressTranslate={suppressReaderNameAutoTranslate}
                      nameClass={styles.userName}
                      topicModChecker={topicModChecker}
                    />
                    <span className={styles.timeAgo}>
                      · {activeReview.createTime || "Just now"}
                    </span>
                  </div>
                </div>
              </div>

              {shouldRenderRatingStars && activeReview.rating > 0 ? (
                <div className={styles.reviewStarBars}>
                  <div className={styles.reviewStarsOnly}>
                    {"★".repeat(activeReview.rating)}
                  </div>
                </div>
              ) : null}

              <div
                className={`${styles.detailHeroText} notranslate`}
                {...noAutoTranslateUserTextProps}
                dangerouslySetInnerHTML={{
                  __html: formatCommentContentForDisplay(activeReview.content),
                }}
              />

              {renderReviewAttachmentGrid(activeReview.attachmentImageUrls)}
              {activeReview.voteId != null && String(activeReview.voteId).trim() !== "" ? (
                <VoteSection
                  voteId={activeReview.voteId}
                  isLoggedIn={isLoggedIn}
                  onRequireLogin={onRequireLogin}
                />
              ) : null}

              <div className={styles.detailHeroFooter}>
                <div className={styles.detailHeroStats}>
                  <span>{activeReplyCount} replies</span>
                </div>
                <div className={styles.actionGroup}>
                  {renderLikeAction(activeReview)}
                  <div className={styles.menuWrapper}>
                    {renderReplyButton(activeReview)}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.detailRepliesSection}>
              <div className={styles.detailRepliesHeader}>
                <h4>
                  {t("comment.replies.title", "Replies ({count})", {
                    count: activeReplyCount,
                  })}
                </h4>
              </div>

              {replyLoading[activeReview.id] && rootReplies.length === 0 ? (
                <div className={styles.loadingState}>
                  {t("comment.loading", "Loading...")}
                </div>
              ) : rootReplies.length > 0 ? (
                <>
                  <div className={styles.detailReplyList}>
                    {renderReplyTree(activeReview, activeReview.id)}
                  </div>
                  {hasMoreDetailReplies ? (
                    <div
                      ref={loadMoreRepliesRef}
                      style={{ height: 1, marginTop: 1, visibility: "hidden" }}
                    />
                  ) : null}
                  {replyLoading[activeReview.id] ? (
                    <div className={styles.loadingState}>
                      {t("comment.loading", "Loading...")}
                    </div>
                  ) : null}
                </>
              ) : activeReplyCount > 0 ? (
                <div className={styles.detailReplyEmpty}>
                  {replyLoading[activeReview.id] ? (
                    <span>{t("comment.loading", "Loading...")}</span>
                  ) : null}
                </div>
              ) : (
                <div className={styles.emptyState}>
                  {t("comment.replies.empty", "Be the first to reply.")}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {comments.map(review => {
              const reviewReplyCount = getReplyCount(review);
              const firstReply = getRootReplies(review)[0] || null;
              const starsToShow =
                shouldRenderRatingStars && review.rating > 0
                  ? review.rating
                  : 0;
              return (
                <div
                  key={review.id}
                  className={`${styles.reviewStreamItem} ${styles.reviewCardClickable}`}
                  onClick={() => openCommentDetail(review.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openCommentDetail(review.id);
                    }
                  }}
                >
                  {starsToShow > 0 ? (
                    <div className={styles.reviewStarBars}>
                      <div className={styles.reviewStarsOnly}>
                        {"★".repeat(starsToShow)}
                      </div>
                    </div>
                  ) : null}
                  <div
                    className={`${styles.reviewPreviewText} notranslate`}
                    {...noAutoTranslateUserTextProps}
                    dangerouslySetInnerHTML={{
                      __html: formatCommentContentForDisplay(review.content),
                    }}
                  />
                  {renderReviewAttachmentGrid(review.attachmentImageUrls, {
                    stopClick: true,
                  })}
                  {review.voteId != null && String(review.voteId).trim() !== "" ? (
                    <VoteSection
                      voteId={review.voteId}
                      isLoggedIn={isLoggedIn}
                      onRequireLogin={onRequireLogin}
                    />
                  ) : null}
                  {reviewReplyCount > 0 ? (
                    <div className={styles.reviewReplyMeta}>
                      <span>{reviewReplyCount} replies</span>
                      {firstReply?.content ? (
                        <div
                          className={`${styles.reviewReplyPreview} notranslate`}
                          {...noAutoTranslateUserTextProps}
                          dangerouslySetInnerHTML={{
                            __html: formatCommentContentForDisplay(
                              firstReply.content,
                            ),
                          }}
                        />
                      ) : null}
                    </div>
                  ) : null}

                  <div className={styles.reviewFooter}>
                    <div className={styles.userInfo}>
                      {renderAvatar(review, styles.userAvatarSmall)}
                      <div className={styles.userMetaInline}>
                        <UserDisplayNameWithModerator
                          displayName={getDisplayName(review)}
                          userId={review.userId ?? review.userID}
                          username={review.username}
                          item={review}
                          suppressTranslate={suppressReaderNameAutoTranslate}
                          nameClass={styles.userName}
                          topicModChecker={topicModChecker}
                        />
                        <span className={styles.timeAgo}>
                          · {review.createTime || "Just now"}
                        </span>
                      </div>
                    </div>
                    <div className={styles.actionGroup}>
                      {renderLikeAction(review)}
                      <div className={styles.menuWrapper}>
                        {renderReplyButton(review)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* 无限下滑触底加载指示器 */}
            <div
              ref={loadMoreRef}
              style={{ height: 1, marginTop: 1, visibility: "hidden" }}
            />
          </>
        )
      ) : (
        <div className={styles.emptyState}>
          {t("comment.list.empty", "-- No comments --")}
        </div>
      )}

      {/* 评论弹窗（一级 + 二级回复复用） */}
      {showCommentModal && (
        <div
          className={styles.modalOverlay}
          onClick={() => {
            setShowCommentModal(false);
            setReplyTo(null);
            setSelectedImages([]);
            setPreviewImageIndex(-1);
            setCommentAttachmentUUID("");
            resetCommentVoteFields();
          }}
        >
          <div
            className={`${styles.modalContent} ${
              isCollectionTopLevelComment ? styles.collectionCommentModal : ""
            }`.trim()}
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button
              className={styles.modalClose}
              onClick={() => {
                setShowCommentModal(false);
                setReplyTo(null);
                setSelectedImages([]);
                setPreviewImageIndex(-1);
                setCommentAttachmentUUID("");
                resetCommentVoteFields();
              }}
            >
              ✕
            </button>
            <div className={styles.modalHeaderBlock}>
              <h3 className={styles.modalTitle}>
                {replyTo ? (
                  <>
                    <span>{t("comment.modal.replyPrefix", "reply @")}</span>
                    <UserDisplayNameWithModerator
                      displayName={replyTo.username}
                      userId={replyTo.userId}
                      username={replyTo.loginUsername}
                      item={null}
                      suppressTranslate={suppressReaderNameAutoTranslate}
                      topicModChecker={topicModChecker}
                    />
                  </>
                ) : (
                  t("comment.modal.writeTitle", "Write a review")
                )}
              </h3>
            </div>

            {/* 仅在一级评论且分类为 GAME 或 COLLECTION 时显示评分 */}
            {!replyTo && category === "GAME" && (
              <div className={styles.ratingSection}>
                <span className={styles.ratingTitle}>
                  {t("comment.modal.ratingTitle", "Rating (Free Choice)")}
                </span>
                <div className={styles.ratingStarsRow}>
                  {[1, 2, 3, 4, 5].map(star => {
                    const isActive = (hoverRating || rating) >= star;
                    return (
                      <svg
                        key={star}
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill={isActive ? "#7a00ff" : "none"}
                        stroke="#7a00ff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={styles.ratingStar}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setRating(star)}
                      >
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                    );
                  })}
                </div>
              </div>
            )}

            <div className={styles.commentRichTextWrapper}>
              {/* placeholder */}
              {(!commentContent || commentContent === "<br>") && (
                <div className={`${styles.editorPlaceholder} ignore`}>
                  {replyTo ? (
                    <>
                      <span>{t("comment.modal.replyPrefix", "reply @")}</span>
                      <UserDisplayNameWithModerator
                        displayName={replyTo.username}
                        userId={replyTo.userId}
                        username={replyTo.loginUsername}
                        item={null}
                        suppressTranslate={suppressReaderNameAutoTranslate}
                        topicModChecker={topicModChecker}
                      />
                      <span>...</span>
                    </>
                  ) : (
                    t("comment.editor.placeholder", "Share your thoughts...")
                  )}
                </div>
              )}

              <div
                ref={editorRef}
                className={`${styles.commentRichText} ignore`}
                contentEditable
                suppressContentEditableWarning
                translate="no"
                data-i18n-ignore="true"
                data-i18n="no"
                onBeforeInput={handleEditorBeforeInput}
                onInput={handleEditorInput}
                onFocus={handleEditorFocus}
              />
              <div
                className={`${styles.commentCharCount} ${
                  isCommentTooLong ? styles.commentCharCountOver : ""
                }`}
                aria-live="polite"
              >
                {t("comment.editor.charCount", "{current}/{max}", {
                  current: draftPlainTextLength,
                  max: COMMENT_MAX_LENGTH,
                })}
              </div>
            </div>
            {selectedImages.length > 0 && (
              <div className={styles.imageThumbGrid}>
                {selectedImages.map(item => (
                  <div
                    key={item.id}
                    className={styles.imageThumbItem}
                    onClick={() =>
                      handleOpenImagePreview(
                        selectedImages.findIndex(img => img.id === item.id),
                      )
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        handleOpenImagePreview(
                          selectedImages.findIndex(img => img.id === item.id),
                        );
                      }
                    }}
                  >
                    <img src={item.src} alt="selected" />
                    <button
                      type="button"
                      className={styles.imageThumbRemove}
                      onClick={e => {
                        e.stopPropagation();
                        handleRemoveSelectedImage(item.id);
                      }}
                      aria-label={t("comment.aria.deleteImage", "Delete image")}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {previewImageIndex >= 0 &&
            previewImageIndex < selectedImages.length ? (
              <div
                className={styles.imagePreviewOverlay}
                onClick={handleCloseImagePreview}
              >
                <div
                  className={styles.imagePreviewContent}
                  onClick={e => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className={`${styles.imagePreviewNav} ${styles.imagePreviewNavLeft}`}
                    onClick={handlePreviewPrev}
                    aria-label={t("aria.prevOne", "previous one")}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className={styles.imagePreviewClose}
                    onClick={handleCloseImagePreview}
                    aria-label={t("aria.closePreview", "close preview")}
                  >
                    ×
                  </button>
                  <img
                    src={selectedImages[previewImageIndex]?.src || ""}
                    alt="preview"
                    className={styles.imagePreviewImage}
                  />
                  <div className={styles.imagePreviewPager}>
                    {previewImageIndex + 1} / {selectedImages.length}
                  </div>
                  <button
                    type="button"
                    className={`${styles.imagePreviewNav} ${styles.imagePreviewNavRight}`}
                    onClick={handlePreviewNext}
                    aria-label={t("aria.nextOne", "next one")}
                  >
                    ›
                  </button>
                </div>
              </div>
            ) : null}
            {isCollectionTopLevelComment && commentAddVote ? (
              <div className={`${styles.pollPanel} ${styles.pollPanelAppear}`}>
                <div className={styles.pollPanelHeader}>
                  <div className={styles.pollPanelTitle}>
                    {t("comment.poll.addPoll", "Add poll")}
                  </div>
                </div>
                <input
                  type="text"
                  className={styles.pollQuestionInput}
                  placeholder={t("comment.poll.questionPlaceholder", "Please enter a poll question")}
                  value={commentVoteTitle}
                  onChange={e => setCommentVoteTitle(e.target.value)}
                  maxLength={200}
                  disabled={submittingComment}
                />
                <div className={styles.pollTypeRow}>
                  <span className={styles.pollTypeLabel}>
                    {t("comment.poll.choiceType", "Choice type")}
                  </span>
                  <label className={styles.pollRadio}>
                    <input
                      type="radio"
                      name="commentVoteChoiceType"
                      checked={commentVoteChoiceType === "SINGLE"}
                      onChange={() => setCommentVoteChoiceType("SINGLE")}
                      disabled={submittingComment}
                    />
                    {t("comment.poll.single", "Single")}
                  </label>
                  <label className={styles.pollRadio}>
                    <input
                      type="radio"
                      name="commentVoteChoiceType"
                      checked={commentVoteChoiceType === "MULTI"}
                      onChange={() => setCommentVoteChoiceType("MULTI")}
                      disabled={submittingComment}
                    />
                    {t("comment.poll.multiple", "Multiple")}
                  </label>
                  {commentVoteChoiceType === "MULTI" ? (
                    <label className={styles.pollMaxRow}>
                      <span>{t("comment.poll.maxChoices", "Max choices")}</span>
                      <input
                        type="number"
                        className={styles.pollMaxInput}
                        min={2}
                        max={Math.max(2, commentVoteOptions.length)}
                        value={commentVoteMaxSelected}
                        onChange={e =>
                          setCommentVoteMaxSelected(Number(e.target.value) || 2)
                        }
                        disabled={submittingComment}
                      />
                    </label>
                  ) : null}
                </div>
                <div className={styles.pollOptionsLabel}>
                  {t("comment.poll.optionsLabel", "Options (2–8)")}
                </div>
                <div className={styles.pollOptionList}>
                  {commentVoteOptions.map((opt, idx) => (
                    <div key={`comment-vote-opt-${idx}`} className={styles.pollOptionRow}>
                      <input
                        type="text"
                        className={styles.pollOptionInput}
                        placeholder={t("comment.poll.optionPlaceholder", "Option {index}", {
                          index: idx + 1,
                        })}
                        value={opt}
                        onChange={e =>
                          handleCommentVoteOptionChange(idx, e.target.value)
                        }
                        maxLength={120}
                        disabled={submittingComment}
                      />
                      <button
                        type="button"
                        className={styles.pollOptionRemove}
                        onClick={() => handleRemoveCommentVoteOption(idx)}
                        disabled={submittingComment || commentVoteOptions.length <= 2}
                        aria-label={t("topic.aria.removeOption", "Remove option")}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <div className={styles.pollFooter}>
                  <button
                    type="button"
                    className={styles.pollAddOption}
                    onClick={handleAddCommentVoteOption}
                    disabled={submittingComment || commentVoteOptions.length >= 8}
                  >
                    {t("comment.poll.addOption", "+ Add option")}
                  </button>
                </div>
              </div>
            ) : null}
            <div className={styles.commentModalActions}>
              <div className={styles.commentModalActionTop}>
                <div className={styles.uploadMediaGroup}>
                  <button
                    type="button"
                    className={styles.richBtn}
                    onClick={handlePickImageClick}
                    aria-label={t("comment.aria.addImage", "Add image")}
                    title="Add image"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="M21 15l-5-5L5 21" />
                    </svg>
                  </button>
                  <span className={styles.uploadMediaHint}>
                    {t("comment.media.uploadHint", "Tap to add upload media files")}
                  </span>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleImageSelected}
                  />
                </div>
                {isCollectionTopLevelComment ? (
                  <label
                    className={`${styles.pollToggleLabel} ${
                      commentAddVote ? styles.pollToggleLabelActive : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      className={styles.pollToggleInput}
                      checked={commentAddVote}
                      onChange={e => setCommentAddVote(e.target.checked)}
                      disabled={submittingComment}
                    />
                    <span className={styles.pollToggleSwitch} aria-hidden="true">
                      <span className={styles.pollToggleThumb} />
                    </span>
                    <span className={styles.pollToggleText}>
                      {t("comment.poll.addPoll", "Add poll")}
                    </span>
                  </label>
                ) : null}
              </div>
              <div className={styles.commentModalActionBottom}>
                <button
                  className={`${styles.submitBtn} ${
                    submittingComment ? styles.submitBtnDisabled : ""
                  }`}
                  onClick={handleSubmitComment}
                  disabled={submittingComment || !canSubmitCommentDraft}
                >
                  {submittingComment
                    ? t("comment.submit.submitting", "Submitting...")
                    : replyTo
                      ? t("comment.submit.reply", "Submit reply")
                      : t("comment.submit.comment", "Submit comment")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {profileTarget ? (
        <UserProfilePanel
          userId={profileTarget.userId}
          fallbackName={profileTarget.username}
          fallbackAvatarUrl={profileTarget.avatarUrl}
          isLoggedIn={isLoggedIn}
          onRequireLogin={onRequireLogin}
          onClose={() => setProfileTarget(null)}
        />
      ) : null}

      {attachmentGalleryPreview &&
      attachmentGalleryPreview.urls.length > 0 ? (
        <div
          className={`${styles.imagePreviewOverlay} ${styles.attachmentGalleryOverlay}`}
          onClick={closeAttachmentGallery}
        >
          <div
            className={styles.imagePreviewContent}
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              className={`${styles.imagePreviewNav} ${styles.imagePreviewNavLeft}`}
              onClick={galleryPrev}
              aria-label={t("aria.prevOne", "previous one")}
            >
              ‹
            </button>
            <button
              type="button"
              className={styles.imagePreviewClose}
              onClick={closeAttachmentGallery}
              aria-label={t("aria.closePreview", "close preview")}
            >
              ×
            </button>
            <img
              src={
                attachmentGalleryPreview.urls[attachmentGalleryPreview.index] ||
                ""
              }
              alt=""
              className={styles.imagePreviewImage}
            />
            <div className={styles.imagePreviewPager}>
              {attachmentGalleryPreview.index + 1} /{" "}
              {attachmentGalleryPreview.urls.length}
            </div>
            <button
              type="button"
              className={`${styles.imagePreviewNav} ${styles.imagePreviewNavRight}`}
              onClick={galleryNext}
              aria-label={t("aria.nextOne", "next one")}
            >
              ›
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CommentSection;
